/* Move24 interactions: native DOM only; safe to reinitialize in the Shopify editor. */
(() => {
  'use strict';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const registered = new WeakMap();
  const selectAll = (scope, selector) => [...scope.querySelectorAll(selector)];
  // Source AOS settings: 800 ms, ease-out, offset 120 px, once per visit.
  // Apply motion only after JavaScript is ready; otherwise content stays visible.
  const reveals = new Map();
  const revealObservers = new Map();
  const revealElement = (element, immediate = false) => {
    const record = reveals.get(element);
    if (!record || (record.shown && !immediate)) return;
    record.shown = true;
    record.observer.unobserve(element);
    clearTimeout(record.timer);
    element.classList.toggle('m24-reveal-running', !immediate);
    element.classList.remove('m24-reveal-pending');
    element.dataset.m24RevealState = immediate ? 'complete' : 'running';
    if (!immediate) record.timer = setTimeout(() => {
      element.classList.remove('m24-reveal-running');
      element.dataset.m24RevealState = 'complete';
    }, record.duration + record.delay + 100);
  };
  const initializeReveals = (scope) => {
    if (reducedMotion.matches || window.Shopify?.designMode || document.body.dataset.m24MotionEnabled === 'false' || !('IntersectionObserver' in window)) return;
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    const prepare = (element, effect = 'fade-up', delay = 0, duration = 800, distance = 100, easing = 'ease-out', offset = 120) => {
      if (!element || reveals.has(element)) return;
      const section = element.closest('[data-m24-section]');
      const mode = section?.dataset.m24Animation || 'original';
      if (mode === 'none' || (mode !== 'original' && element !== section)) return;
      if (mode !== 'original') {
        effect = mode;
        duration = Number(section.dataset.m24Duration) || 800;
        delay = Number(section.dataset.m24Delay) || 0;
        distance = Number(section.dataset.m24Distance) || 100;
      }
      const vertical = effect === 'fade-up' ? distance : 0;
      // IntersectionObserver measures the transformed rectangle. Compensate so
      // the untransformed top crosses the same trigger line as the source.
      const margin = vertical - offset;
      let observer = revealObservers.get(margin);
      if (!observer) {
        observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (entry.isIntersecting || entry.boundingClientRect.bottom < 0) revealElement(entry.target);
        }), { rootMargin: `0px 0px ${margin}px 0px`, threshold: 0 });
        revealObservers.set(margin, observer);
      }
      element.dataset.m24Reveal = effect;
      element.dataset.m24RevealDelay = String(delay);
      element.dataset.m24RevealDuration = String(duration);
      element.dataset.m24RevealState = 'pending';
      element.style.setProperty('--m24-reveal-x', `${effect === 'fade-right' ? -distance : effect === 'fade-left' ? distance : 0}px`);
      element.style.setProperty('--m24-reveal-y', `${vertical}px`);
      element.style.setProperty('--m24-reveal-duration', `${duration}ms`);
      element.style.setProperty('--m24-reveal-delay', `${delay}ms`);
      element.style.setProperty('--m24-reveal-easing', easing);
      element.classList.add('m24-reveal-pending');
      reveals.set(element, { observer, duration, delay, shown: false, timer: null });
      observer.observe(element);
    };
    selectAll(scope, '[data-m24-section]').forEach(element => {
      if (['fade-up', 'fade-left', 'fade-right'].includes(element.dataset.m24Animation)) prepare(element);
    });
    selectAll(scope, '.m24-hero h1').forEach(element => mobile
      ? prepare(element, 'fade-up', 0, 500, 10, 'ease', Math.round(innerHeight * .1))
      : prepare(element, 'fade-up', 50));
    if (mobile) selectAll(scope, '.m24-banner h1').forEach(element => prepare(element, 'fade-up', 0, 500, 10, 'ease', Math.round(innerHeight * .1)));
    selectAll(scope, '.m24-hero .m24-actions').forEach(element => prepare(element, 'fade-up', 500));
    selectAll(scope, '.m24-split-photo').forEach(element => prepare(element, element.closest('.m24-split-right') ? 'fade-left' : 'fade-right', 500));
    selectAll(scope, '.m24-split-copy > h2, .m24-contact-form h2').forEach(element => prepare(element, 'fade-up', 0, 500));
    selectAll(scope, '.m24-services, .m24-faq-grid').forEach(element => prepare(element.parentElement.querySelector('.m24-heading-center h2'), 'fade-up', 0, 500));
    selectAll(scope, '[data-reviews] .m24-heading-center h2').forEach(element => prepare(element, 'fade-up', 0, 200));
    selectAll(scope, '.m24-areas, .m24-related').forEach(element => prepare(element, 'fade-up', 500));
    selectAll(scope, '.m24-callout').forEach(element => prepare(element, 'fade-up', element.closest('.m24-callouts-grid') ? 1000 : 500));
    selectAll(scope, '.m24-cta').forEach(element => prepare(element, 'fade-up', 600));
    selectAll(scope, '.m24-faq-footer > p').forEach(element => prepare(element, 'fade-up', 50));
    selectAll(scope, '.m24-faq-footer > .m24-button').forEach(element => prepare(element, 'fade-up', 70));
  };
  const removeReveals = (scope) => selectAll(scope, '[data-m24-reveal]').forEach(element => {
    revealElement(element, true);
    reveals.delete(element);
  });
  // Keyboard navigation must never wait for an entrance animation.
  document.addEventListener('focusin', event => {
    let element = event.target.closest('[data-m24-reveal]');
    while (element) { revealElement(element, true); element = element.parentElement?.closest('[data-m24-reveal]'); }
  });
  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) reveals.forEach((_, element) => revealElement(element, true));
    else initializeReveals(document);
  });
  const initialize = (scope = document) => {
    initializeReveals(scope);
    selectAll(scope, '[data-mobile-menu]').forEach(menu => {
      if (registered.has(menu)) return;
      const abort = new AbortController();
      registered.set(menu, () => abort.abort());
      const summary = menu.querySelector('summary');
      const sync = () => {
        summary.setAttribute('aria-expanded', String(menu.open));
        summary.setAttribute('aria-label', menu.open ? 'Închide meniul' : 'Deschide meniul');
      };
      menu.addEventListener('toggle', sync, { signal: abort.signal });
      menu.addEventListener('click', e => { if (e.target.closest('a')) menu.open = false; }, { signal: abort.signal });
      document.addEventListener('keydown', e => { if (e.key === 'Escape' && menu.open) { menu.open = false; summary.focus(); } }, { signal: abort.signal });
      document.addEventListener('click', e => { if (!menu.contains(e.target)) menu.open = false; }, { signal: abort.signal });
      sync();
    });
    selectAll(scope, '[data-gallery]').forEach(gallery => {
      if (registered.has(gallery)) return;
      const abort = new AbortController();
      const opts = { signal: abort.signal };
      const items = selectAll(gallery, '[data-gallery-item]');
      const initial = Math.max(1, Number(gallery.dataset.initial) || 8);
      const step = Math.max(1, Number(gallery.dataset.step) || 4);
      const more = gallery.querySelector('[data-gallery-more]');
      const status = gallery.querySelector('[data-gallery-status]');
      const dialog = gallery.querySelector('[data-lightbox]');
      let visible = initial;
      let active = 0;
      let opener;
      const update = () => {
        items.forEach((item, i) => { item.hidden = i >= visible; });
        if (more) more.hidden = visible >= items.length;
        if (status) status.textContent = `${Math.min(visible, items.length)} din ${items.length} fotografii afișate`;
      };
      const show = index => {
        if (!items.length) return;
        active = (index + items.length) % items.length;
        const item = items[active];
        const photo = dialog.querySelector('[data-lightbox-image]');
        photo.src = item.href;
        photo.alt = item.dataset.caption || '';
        dialog.querySelector('[data-lightbox-caption]').textContent = photo.alt;
        dialog.querySelector('[data-lightbox-counter]').textContent = `${active + 1} / ${items.length}`;
      };
      more?.addEventListener('click', () => {
        const firstNew = visible;
        visible += step;
        update();
        items[firstNew]?.focus({ preventScroll: true });
      }, opts);
      if (dialog && typeof dialog.showModal === 'function') {
        items.forEach((item, index) => item.addEventListener('click', e => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault(); opener = item; show(index); dialog.showModal(); document.body.classList.add('m24-dialog-open');
        }, opts));
        dialog.querySelector('[data-lightbox-close]').addEventListener('click', () => dialog.close(), opts);
        dialog.querySelector('[data-lightbox-prev]').addEventListener('click', () => show(active - 1), opts);
        dialog.querySelector('[data-lightbox-next]').addEventListener('click', () => show(active + 1), opts);
        dialog.addEventListener('keydown', e => { if (e.key === 'ArrowLeft') { e.preventDefault(); show(active - 1); } if (e.key === 'ArrowRight') { e.preventDefault(); show(active + 1); } }, opts);
        dialog.addEventListener('click', e => { if (e.target === dialog) dialog.close(); }, opts);
        dialog.addEventListener('close', () => { document.body.classList.remove('m24-dialog-open'); opener?.focus({ preventScroll: true }); }, opts);
      }
      gallery.addEventListener('shopify:block:select', e => {
        const selected = items.findIndex(item => item.contains(e.target) || item === e.target);
        if (selected >= visible) { visible = selected + 1; update(); }
      }, opts);
      update();
      registered.set(gallery, () => { if (dialog?.open) dialog.close(); abort.abort(); });
    });
    selectAll(scope, '[data-reviews]').forEach(root => {
      if (registered.has(root)) return;
      const abort = new AbortController();
      const opts = { signal: abort.signal };
      const track = root.querySelector('[data-review-track]');
      const prev = root.querySelector('[data-review-prev]');
      const next = root.querySelector('[data-review-next]');
      const pause = root.querySelector('[data-review-pause]');
      let timer;
      let paused = false;
      let hovering = false;
      let focused = false;
      const advance = direction => {
        const card = track.firstElementChild;
        if (!card) return;
        const distance = card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).gap || '0');
        const max = track.scrollWidth - track.clientWidth;
        const position = direction > 0 && track.scrollLeft >= max - 4 ? 0 : direction < 0 && track.scrollLeft <= 4 ? max : track.scrollLeft + direction * distance;
        track.scrollTo({ left: position, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      };
      const schedule = () => {
        clearInterval(timer);
        if (root.dataset.autoplay === 'true' && !reducedMotion.matches && !paused && !hovering && !focused && !document.hidden && !window.Shopify?.designMode) timer = setInterval(() => advance(1), 5000);
      };
      const pauseUser = () => {
        paused = true;
        if (pause) { pause.setAttribute('aria-pressed', 'true'); pause.textContent = 'Pornește derularea automată'; }
        schedule();
      };
      if (prev && next) {
        prev.hidden = next.hidden = false;
        prev.addEventListener('click', () => { pauseUser(); advance(-1); }, opts);
        next.addEventListener('click', () => { pauseUser(); advance(1); }, opts);
      }
      if (pause) {
        pause.hidden = reducedMotion.matches;
        pause.addEventListener('click', () => { paused = !paused; pause.setAttribute('aria-pressed', String(paused)); pause.textContent = paused ? 'Pornește derularea automată' : 'Oprește derularea automată'; schedule(); }, opts);
      }
      root.addEventListener('mouseenter', () => { hovering = true; schedule(); }, opts);
      root.addEventListener('mouseleave', () => { hovering = false; schedule(); }, opts);
      root.addEventListener('focusin', () => { focused = true; schedule(); }, opts);
      root.addEventListener('focusout', e => { if (!root.contains(e.relatedTarget)) { focused = false; schedule(); } }, opts);
      root.addEventListener('shopify:block:select', e => {
        const review = e.target.closest('.m24-review');
        if (review && track.contains(review)) {
          pauseUser();
          review.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'center' });
        }
      }, opts);
      track.addEventListener('pointerdown', pauseUser, opts);
      document.addEventListener('visibilitychange', schedule, opts);
      reducedMotion.addEventListener('change', schedule, opts);
      schedule();
      registered.set(root, () => { clearInterval(timer); abort.abort(); });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize()); else initialize();
  document.addEventListener('shopify:section:load', e => initialize(e.target));
  document.addEventListener('shopify:block:select', e => {
    const question = e.target.closest('.m24-faq');
    if (question) question.open = true;
  });
  document.addEventListener('shopify:section:unload', e => {
    removeReveals(e.target);
    selectAll(e.target, '[data-gallery],[data-reviews],[data-mobile-menu]').forEach(el => { registered.get(el)?.(); registered.delete(el); });
  });
})();
